/**
 * Frontend Date Utilities
 * 
 * These utilities ensure dates are handled consistently across the frontend,
 * avoiding timezone bugs and off-by-one errors when working with the backend.
 */

/**
 * Converts a Date object to ISO string format for API submission
 * Always uses UTC midnight to avoid timezone issues
 */
export function toUTCDateString(date: Date): string {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  // Create UTC date at midnight
  const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  return utcDate.toISOString();
}

/**
 * Parses a date from the backend (ISO string) to a Date object
 * Preserves the calendar date regardless of timezone
 */
export function fromUTCDateString(dateString: string): Date {
  const date = new Date(dateString);
  
  // Extract UTC date components
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  
  // Return as local date with same calendar values
  return new Date(year, month, day);
}

/**
 * Creates a Date object at local midnight for calendar pickers
 * This ensures the calendar shows the correct date
 */
export function createLocalDate(year: number, month: number, day: number): Date {
  return new Date(year, month, day, 0, 0, 0, 0);
}

/**
 * Formats a date for display (matches formatDate from utils.ts)
 */
export function formatDisplayDate(date: Date | string): string {
  const d = typeof date === 'string' ? fromUTCDateString(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Adds months to a date, preserving day of month
 */
export function addMonths(date: Date, months: number): Date {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  const newMonth = month + months;
  const newYear = year + Math.floor(newMonth / 12);
  const finalMonth = ((newMonth % 12) + 12) % 12;
  
  // Handle day overflow (e.g., Jan 31 -> Feb 28/29)
  const daysInNewMonth = new Date(newYear, finalMonth + 1, 0).getDate();
  const finalDay = Math.min(day, daysInNewMonth);
  
  return new Date(newYear, finalMonth, finalDay);
}

/**
 * Adds years to a date, preserving day of month
 */
export function addYears(date: Date, years: number): Date {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  const newYear = year + years;
  
  // Handle Feb 29 on non-leap years
  const daysInNewMonth = new Date(newYear, month + 1, 0).getDate();
  const finalDay = Math.min(day, daysInNewMonth);
  
  return new Date(newYear, month, finalDay);
}
